import React, { useReducer, useEffect, useCallback } from 'react';
import axios from 'axios';
import { queryCache } from 'react-query';
import { useHistory } from 'react-router-dom';
import { isTokenExpired } from 'utils/tools';
import FullPageLoading from 'components/full-page-loading';

const AuthContext = React.createContext();
const storageKey = 'user';

const baseURL = process.env.REACT_APP_API_URL;
const reducer = (state, action) => {
  switch (action.type) {
    case 'FETCH_USER':
      return { ...state, user: null, loading: true };
    case 'SET_USER':
      return { ...state, user: action.payload.user, loading: false };
    case 'REMOVE_USER':
      return { ...state, user: null, loading: false };
    default:
      throw new Error('Action not supported');
  }
};

const initialState = {
  user: null,
  loading: true,
};

function AuthProvider(props) {
  const history = useHistory();
  const [state, dispatch] = useReducer(reducer, initialState);

  const validateToken = useCallback(() => {
    let storedUser = localStorage.getItem(storageKey);

    if (storedUser === null) {
      dispatch({ type: 'SET_USER', payload: { user: null } });
      return null;
    }

    try {
      const parsed = JSON.parse(storedUser);
      if (parsed.access_token == null) {
        throw new Error('No access_token key');
      }
    } catch (e) {
      storedUser = null;
      localStorage.removeItem(storageKey);
      console.warn('Error parsing user token');
      console.error(e);
    }

    const user = storedUser ? JSON.parse(storedUser) : null;

    if (user == null) {
      return dispatch({ type: 'SET_USER', payload: { user: null } });
    }

    dispatch({ type: 'FETCH_USER' });

    return axios
      .post(
        `${baseURL}/login/test-token`,
        {},
        {
          headers: {
            Authorization: `Bearer ${user.access_token}`,
          },
        }
      )
      .then(() => {
        dispatch({ type: 'SET_USER', payload: { user } });
      })
      .catch(() => {
        console.info('User token invalid, removing ...');
        dispatch({ type: 'REMOVE_USER' });
        localStorage.removeItem(storageKey);
        history.push('/');
      });
  }, [history]);

  useEffect(() => {
    validateToken();
  }, [validateToken]);

  if (state.loading) {
    return <FullPageLoading header={false} footer={false} />;
  }

  function handleUser(res) {
    if (!res.data) {
      console.error('Response is null');
    } else {
      const { data: user } = res;

      localStorage.setItem(storageKey, JSON.stringify(user));
      dispatch({ type: 'SET_USER', payload: { user } });
    }
  }

  function login(values) {
    const data = new FormData();

    Object.entries(values).forEach(([key, value]) => {
      data.append(key, value);
    });

    return axios
      .post(`${baseURL}/login/access-token`, data, {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
      })
      .then(handleUser)
      .catch((error) => Promise.reject(error));
  }

  function resetPasswordRequest(values) {
    return axios.post(`${baseURL}/request-password-reset/`, values);
  }

  function resetPassword(values) {
    const { token, password } = values;
    return axios.post(`${baseURL}/password-reset/`, {
      token,
      new_password: password,
    });
  }

  function resetPasswordValidate(values) {
    return axios.get(
      `${baseURL}/forget-password/confirmation/${values.uid}/${values.token}/`,
      values
    );
  }

  function confirmEmail(values) {
    return axios.post(`${baseURL}/confirm-email/`, values);
  }

  function register(values) {
    return axios.post(`${baseURL}/users/open`, values).catch((error) => {
      return Promise.reject(error);
    });
  }

  function logout() {
    dispatch({ type: 'REMOVE_USER' });
    queryCache.clear();
    history.push('/');
    localStorage.removeItem(storageKey);
  }

  return (
    <AuthContext.Provider
      value={{
        user: state.user,
        login,
        logout,
        register,
        validateToken,
        resetPassword,
        resetPasswordRequest,
        resetPasswordValidate,
        confirmEmail,
      }}
      {...props}
    />
  );
}

const useAuth = () => React.useContext(AuthContext);

export { AuthProvider, useAuth, isTokenExpired };
